import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import config from "../config";
import { MissionEnums } from "../common/constants";
import Task from "../common/Task";
import RegistrationDialog from "../components/common/dialogs/RegistrationDialog";
import RouteCollection from "../common/RouteCollection";
import Location from "../common/Location";
import ConfirmationDialog from "../components/common/dialogs/ConfirmationDialog";
import SubmitErrorDialog from "../components/common/dialogs/SubmitErrorDialog";

export const FormContext = createContext();

export const useForm = () => useContext(FormContext);

let PRINTED_ERROR = false;

export const FormProvider = (props) => {
  const { linkId } = props;

  // State
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState(-1);

  const [missionType, setMissionType] = useState(null);
  const [disableMissionTypeSelect, setDisableMissionTypeSelect] = useState(false);


  // Contact data
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhoneNumber, setContactPhoneNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  // Target data
  const [targetType, setTargetType] = useState(MissionEnums.TargetType.Car);
  const [targetDescription, setTargetDescription] = useState("");
  const [disableTargetTypeSelect, setDisableTargetTypeSelect] = useState(false);
  const [targetTypeOptions, setTargetTypeOptions] = useState([]);

  const [registration, setRegistration] = useState("");
  const [vin, setVin] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");

  const [regAction, setRegAction] = useState(null);
  const [regData, setRegData] = useState(null);

  // Route Data
  const [geolocation, setGeolocation] = useState(null);
  const [routeCollection, setRouteCollection] = useState(null);
  const route = routeCollection?.getRoute("main");
  const [routeRevision, setRouteRevision] = useState(0);
  const [routeDetails, setRouteDetails] = useState("")

  // Details Data
  const [message, setMessage] = useState("");
  const [waitingAtLocation, setWaitingAtLocation] = useState(0);
  const [expectedDone, setExpectedDone] = useState("");
  const [keyPlacement, setKeyPlacement] = useState("");

  const getGeolocation = useCallback(async () => {
    try {
      const t = new Task();

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(loc => {

          const geoloc = {
            timestamp: Date.now(),
            coords: {
              latitude: loc.coords?.latitude,
              longitude: loc.coords?.longitude,
              accuracy: loc.coords?.accuracy || 50,
              heading: loc.coords?.heading,
              speed: loc.coords?.speed,
            }
          };

          setGeolocation(geoloc);
          t.complete(geoloc);
        }, err => {
          if (!PRINTED_ERROR) {
            PRINTED_ERROR = true;
            console.log(err);
          }
          t.complete(null);
        }, { maximumAge: 15000, timeout: 10000, enableHighAccuracy: true });
      } else {
        setTimeout(() => t.complete(null), 1);
      }

      return t.wait();
    } catch (err) {
      console.log(err);
    }
  }, []);


  useEffect(() => {
    if (!!linkId) {
      let active = true;
      let rt = null;
      let updateRoute = () => setRouteRevision(t => t + 1);
      getGeolocation();

      axios(`${config.apiUrl}/api/missionform/public/${linkId}`)
        .then(res => {
          if (!active) return;

          let f = res.data;

          let missionTypes = [
            (!!f.enableSalvageMissions) * 1,
            (!!f.enableTowingMissions) * 2,
            (!!f.enableOtherMissions) * 3,
          ].filter(t => t > 0);

          setDisableMissionTypeSelect(missionTypes.length <= 1);
          setMissionType(missionTypes.length === 0 ? MissionEnums.Type.Other : missionTypes[0]);


          let targetTypes = [
            (!!f.enableCarTargets) * 1,
            (!!f.enableBoatTargets) * 2,
            (!!f.enableOtherTargets) * 3
          ].filter(t => t > 0);

          setTargetType(targetTypes.length === 0 ? MissionEnums.TargetType.Car : targetTypes[0]);
          setDisableTargetTypeSelect(targetTypes.length === 1);

          if (targetTypes.length === 0) {
            setTargetTypeOptions([MissionEnums.TargetType.Car, MissionEnums.TargetType.Boat, MissionEnums.TargetType.Other])
          } else {
            setTargetTypeOptions(targetTypes);
          }


          const rc = new RouteCollection(!!f.stationLocation ? new Location({ ...f.stationLocation, title: f.stationName }) : null);
          setRouteCollection(rc);

          rt = rc.getRoute("main");
          rt.on("update", updateRoute);

          setForm(f);
          setLoading(false);
        })
        .catch(err => {
          setNotFound(true)
          setLoading(false);
          console.log(err);
        });

      return () => {
        active = false;
        rt?.off("update", updateRoute);
      };

    } else {
      setNotFound(true);
      setLoading(false);
    }
  }, [linkId, getGeolocation]);


  const fetchRegistrationData = useCallback(async () => {
    const t = new Task();
    setRegAction(t);

    try {
      const data = await t.wait();
      setRegData(data);
      setRegistration(data.registration);
      setVin(data.chassisNumber);
      setMake(data.make);
      setModel(data.model);

    } catch (err) {
      console.log(err);
    } finally {
      setRegAction(null);
    }
  }, []);


  const submitForm = useCallback(async () => {
    try {
      setSubmitting(true);

      const request = {
        // mission type
        missionType,

        // contact
        contactPerson,
        contactPhoneNumber,
        customerName,
        customerEmail,

        // target
        targetType,
        targetDescription,
        registration,
        chassisNumber: vin,
        make,
        model,

        // route
        waypoints: route.locations.map(t => t.getData()),
        routeDetails,

        // extra details
        message,
        waitingAtLocation,
        expectedDone,
        keyPlacement,
      };

      const res = await axios.post(`${config.apiUrl}/api/missionform/public/${linkId}`, request);

      setReferenceNumber(res.data.referenceNumber);
      setSuccess(true);

    } catch (err) {
      console.log(err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }, [
    linkId,
    missionType,
    contactPerson,
    contactPhoneNumber,
    customerName,
    customerEmail,
    targetType,
    targetDescription,
    registration,
    vin,
    make,
    model,
    route,
    routeDetails,
    message,
    waitingAtLocation,
    expectedDone,
    keyPlacement,
  ]);

  const canSubmit = !submitting && !!customerEmail && !!customerName;
  const locked = submitting || loading;

  const makeNewEmptyForm = useCallback(() => {
    window?.location?.reload();
  }, []);

  const copyForm = useCallback(() => {
    setSuccess(false);
    setSubmitError(false);
  }, [])

  return (
    <FormContext.Provider
      value={{
        form,
        loading,
        submitting,
        locked,
        canSubmit,
        referenceNumber,
        submitForm,
        makeNewEmptyForm,
        copyForm,
        notFound,
        fetchRegistrationData,
        disableMissionTypeSelect,
        missionType,
        setMissionType,
        contactPerson,
        setContactPerson,
        contactPhoneNumber,
        setContactPhoneNumber,
        customerName,
        setCustomerName,
        customerEmail,
        setCustomerEmail,
        disableTargetTypeSelect,
        targetTypeOptions,
        targetType,
        setTargetType,
        targetDescription,
        setTargetDescription,
        registration,
        setRegistration,
        vin,
        setVin,
        make,
        setMake,
        route,
        model,
        setModel,
        stationLocation: form?.stationLocation,
        routeDetails,
        setRouteDetails,
        geolocation,
        getGeolocation,
        routeRevision,
        message,
        setMessage,
        waitingAtLocation,
        setWaitingAtLocation,
        expectedDone,
        setExpectedDone,
        keyPlacement,
        setKeyPlacement,
      }}
    >
      {props.children}
      {!!regAction ? <RegistrationDialog task={regAction} registration={registration} /> : null}
      {success ? <ConfirmationDialog /> : null}
      {submitError ? <SubmitErrorDialog /> : null}
    </FormContext.Provider>
  );
};
